/**
 * Subscription API Client
 * 
 * Client for interacting with the subscription API.
 * Based on docs/setup/FRONTEND_SUBSCRIPTION_API_ENDPOINTS.md
 */

import { getJWTToken } from '../auth/token-storage.js';
import { log } from '../logging/index.js';
import { handleFetchError, handleHTTPError } from '../error-handler/index.js';

// Subscription API runs on port 8790 locally (see subscription-api/package.json: "dev": "wrangler dev --port 8790")
const SUBSCRIPTION_API_BASE_URL = 
  import.meta.env.DEV 
    ? 'http://localhost:8790' 
    : 'https://pivot-is-subscription-api.dont-d23.workers.dev';

export interface SubscriptionPlan {
  id?: string;
  name: string;
  slug: 'free' | 'regular' | 'pro';
  price: number; // Price in euros (display value)
  price_monthly?: number; // Price in cents (from API)
  currency: string;
}

export interface Subscription {
  id: string;
  status: 'active' | 'cancelled' | 'past_due' | 'unpaid';
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: number;
  cancelled_at: string | null;
  plan_name: string;
  plan_slug: 'free' | 'regular' | 'pro';
  price_monthly: number;
  currency: string;
}

export interface SubscriptionResponse {
  success: boolean;
  data?: {
    subscription?: Subscription | null;
    plan: SubscriptionPlan;
    status?: string;
  };
  error?: string;
}

export interface CheckoutResponse {
  success: boolean;
  data?: {
    checkoutUrl: string;
    sessionId: string;
  };
  error?: string;
}

export interface PortalResponse {
  success: boolean;
  data?: {
    portalUrl: string;
  };
  error?: string;
}

export interface TopUpResponse {
  success: boolean;
  data?: {
    checkoutUrl: string;
    sessionId: string;
  };
  error?: string;
}

/**
 * Get current subscription status
 */
export async function getCurrentSubscription(): Promise<SubscriptionResponse> {
  const token = getJWTToken();
  if (!token) {
    return {
      success: false,
      error: 'Authentication required'
    };
  }

  try {
    const response = await fetch(`${SUBSCRIPTION_API_BASE_URL}/subscription`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      // Handle HTTP error (will show toast notification and return error message)
      const errorMessage = await handleHTTPError(response, 'Subscription API');
      
      // Try to get more details from response (response body already consumed by handleHTTPError, so clone first)
      let errorData: { error?: string } = { error: errorMessage };
      try {
        const clonedResponse = response.clone();
        errorData = await clonedResponse.json().catch(() => ({ error: errorMessage }));
      } catch {
        // If cloning/parsing fails, use the message from handleHTTPError
        errorData = { error: errorMessage };
      }
      
      return {
        success: false,
        error: errorData.error || errorMessage || `HTTP ${response.status}`
      };
    }

    const data = await response.json();
    
    // Convert price from cents to euros for display
    if (data.success && data.data?.plan) {
      if (data.data.plan.price_monthly !== undefined) {
        data.data.plan.price = data.data.plan.price_monthly / 100;
      }
    }
    
    return data;
  } catch (error) {
    // Handle fetch error (will show toast notification)
    handleFetchError(error, 'Subscription API');
    
    log.error('[SubscriptionAPI] Error getting subscription:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error'
    };
  }
}

/**
 * Create checkout session for a plan
 */
export async function createCheckoutSession(planSlug: 'regular' | 'pro'): Promise<CheckoutResponse> {
  const token = getJWTToken();
  if (!token) {
    return {
      success: false,
      error: 'Authentication required'
    };
  }

  try {
    const response = await fetch(`${SUBSCRIPTION_API_BASE_URL}/checkout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ planSlug })
    });

    if (!response.ok) {
      // Handle HTTP error (will show toast notification and return error message)
      const errorMessage = await handleHTTPError(response, 'Subscription API');
      
      // Try to get more details from response (response body already consumed by handleHTTPError, so clone first)
      let errorData: { error?: string } = { error: errorMessage };
      try {
        const clonedResponse = response.clone();
        errorData = await clonedResponse.json().catch(() => ({ error: errorMessage }));
      } catch {
        // If cloning/parsing fails, use the message from handleHTTPError
        errorData = { error: errorMessage };
      }
      
      return {
        success: false,
        error: errorData.error || errorMessage || `HTTP ${response.status}`
      };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // Handle fetch error (will show toast notification)
    handleFetchError(error, 'Subscription API');
    
    log.error('[SubscriptionAPI] Error creating checkout session:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error'
    };
  }
}

/**
 * Create customer portal session
 */
export async function createPortalSession(): Promise<PortalResponse> {
  const token = getJWTToken();
  if (!token) {
    return {
      success: false,
      error: 'Authentication required'
    };
  }

  try {
    const response = await fetch(`${SUBSCRIPTION_API_BASE_URL}/portal`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      // Handle HTTP error (will show toast notification and return error message)
      const errorMessage = await handleHTTPError(response, 'Subscription API');
      
      // Try to get more details from response (response body already consumed by handleHTTPError, so clone first)
      let errorData: { error?: string } = { error: errorMessage };
      try {
        const clonedResponse = response.clone();
        errorData = await clonedResponse.json().catch(() => ({ error: errorMessage }));
      } catch {
        // If cloning/parsing fails, use the message from handleHTTPError
        errorData = { error: errorMessage };
      }
      
      return {
        success: false,
        error: errorData.error || errorMessage || `HTTP ${response.status}`
      };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // Handle fetch error (will show toast notification)
    handleFetchError(error, 'Network error');
    
    log.error('[SubscriptionAPI] Error creating portal session:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error'
    };
  }
}

/**
 * Create top-up checkout session
 */
export async function createTopUpSession(amountCents: number): Promise<TopUpResponse> {
  const token = getJWTToken();
  if (!token) {
    return {
      success: false,
      error: 'Authentication required'
    };
  }

  try {
    const response = await fetch(`${SUBSCRIPTION_API_BASE_URL}/topup`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ amountCents })
    });

    if (!response.ok) {
      const errorMessage = await handleHTTPError(response, 'Subscription API');
      let errorData: { error?: string } = { error: errorMessage };
      try {
        const clonedResponse = response.clone();
        errorData = await clonedResponse.json().catch(() => ({ error: errorMessage }));
      } catch {
        errorData = { error: errorMessage };
      }
      
      return {
        success: false,
        error: errorData.error || errorMessage || `HTTP ${response.status}`
      };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    handleFetchError(error, 'Subscription API');
    log.error('[SubscriptionAPI] Error creating top-up session:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error'
    };
  }
}

/**
 * Health check
 */
export async function healthCheck(): Promise<{ status: string; service: string; timestamp: string } | null> {
  try {
    const response = await fetch(`${SUBSCRIPTION_API_BASE_URL}/health`);
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch (error) {
    log.error('[SubscriptionAPI] Health check failed:', error);
    return null;
  }
}

